import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot, FaTimes, FaPaperPlane, FaWhatsapp } from 'react-icons/fa';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    issue: ''
  });
  const [currentInput, setCurrentInput] = useState('');
  const [messages, setMessages] = useState([
    { text: "Hi! I'm Nandy, your virtual assistant. How can I help you today?", isBot: true },
    { text: "What is your full name?", isBot: true }
  ]);

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!currentInput.trim()) return;

    // Add user message
    const newMessages = [...messages, { text: currentInput, isBot: false }];
    
    // Logic for next bot message
    let nextBotMessage = '';
    let nextStep = step + 1;

    if (step === 0) {
      setFormData({ ...formData, name: currentInput });
      nextBotMessage = `Nice to meet you, ${currentInput}! What is your phone number?`;
    } else if (step === 1) {
      setFormData({ ...formData, phone: currentInput });
      nextBotMessage = "Got it. What is your full address for the service visit?";
    } else if (step === 2) {
      setFormData({ ...formData, address: currentInput });
      nextBotMessage = "Almost done! Please describe the issue you're facing with your appliance.";
    } else if (step === 3) {
      setFormData({ ...formData, issue: currentInput });
      nextBotMessage = "Thank you! Our technician will contact you shortly! Redirecting you to WhatsApp...";
      
      // Final message and redirection
      setTimeout(() => {
        handleWhatsAppRedirect(currentInput); 
      }, 2000);
    }

    setMessages([...newMessages, { text: nextBotMessage, isBot: true }]);
    setStep(nextStep);
    setCurrentInput('');
  };

  const handleWhatsAppRedirect = (finalIssue) => {
    const { name, phone, address } = formData;
    const issue = finalIssue || formData.issue;
    
    const message = `*New Service Request (Chatbot)*%0A%0A*Name:* ${name}%0A*Phone:* ${phone}%0A*Address:* ${address}%0A*Issue:* ${issue}`;
    const whatsappURL = `https://wa.me/919342851992?text=${message}`;
    
    window.open(whatsappURL, '_blank');
    setStep(0); // Reset for next time
    setIsOpen(false);
    setMessages([
      { text: "Hi! I'm Nandy, your virtual assistant. How can I help you today?", isBot: true },
      { text: "What is your full name?", isBot: true }
    ]);
    setFormData({ name: '', phone: '', address: '', issue: '' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="nandy-chatbot-container">
      {/* Floating Robot Icon */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="nandy-robot-trigger"
            onClick={() => setIsOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              y: [0, -10, 0], // Floating animation
              x: [0, -2, 2, -2, 2, 0] // Vibrate/Shake animation
            }}
            transition={{ 
              scale: { type: 'spring', stiffness: 260, damping: 20 },
              y: { repeat: Infinity, duration: 3, ease: "easeInOut" },
              x: { repeat: Infinity, repeatDelay: 5, duration: 0.5 } // Vibrate every 5 seconds
            }}
            whileHover={{ scale: 1.1 }}
          >
            {/* Pop-up Message */}
            <motion.div 
              className="nandy-pop-message"
              initial={{ opacity: 0, x: 20, scale: 0.5 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              Im ready to help!
            </motion.div>

            <div className="nandy-avatar-wrapper">
              <FaRobot className="nandy-icon" />
              <div className="nandy-status-dot"></div>
            </div>
            <div className="nandy-tooltip">Chat with Nandy!</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="nandy-chat-window"
            initial={{ y: 100, opacity: 0, scale: 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="nandy-header">
              <div className="nandy-header-info">
                <FaRobot />
                <div>
                  <h4>Nandy</h4>
                  <span>Online | Service Expert</span>
                </div>
              </div>
              <button className="nandy-close-btn" onClick={() => setIsOpen(false)}>
                <FaTimes />
              </button>
            </div>

            {/* Messages Area */}
            <div className="nandy-messages-area">
              {messages.map((msg, idx) => (
                <div key={idx} className={`nandy-msg-wrapper ${msg.isBot ? 'bot' : 'user'}`}>
                  <div className="nandy-msg-bubble">
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Input Area */}
            {step <= 3 && (
              <div className="nandy-input-area">
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  autoFocus
                />
                <button className="nandy-send-btn" onClick={handleSend}>
                  <FaPaperPlane />
                </button>
              </div>
            )}

            {step > 3 && (
              <div className="nandy-success-area">
                <FaWhatsapp className="whatsapp-anim" />
                <p>Redirecting to WhatsApp...</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Chatbot;
